import { formatNumberSmart, formatDate as formatSharedDate } from '@/lib/utils'

export const formatDate = (value) => formatSharedDate(value, { fallback: '—' })


export const formatNumber = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return '—'
  return `${formatNumberSmart(num, 2)}${suffix}`
}

export const diffField = (rows, field) => {
  const values = rows
    .map((row) => Number(row[field]))
    .filter((val) => !Number.isNaN(val))
  if (values.length === 0) return null
  if (values.length === 1) return values[0]
  return values[0] - values[values.length - 1]
}

export const toInputDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

export const formatForApiDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const mm = `${d.getMonth() + 1}`.padStart(2, '0')
  const dd = `${d.getDate()}`.padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${mm}-${dd}-${yyyy}`
}

export const toIsoDateString = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toISOString()
}

export const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

export const isNotFoundError = (message = '') => /not found/i.test(String(message))

export const createEmptyMeasurements = () => ({ compositions: [], circumferences: [], vitals: [] })

export const dayOrder = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa']
export const dayLabels = { su: 'Su', mo: 'Mo', tu: 'Tu', we: 'We', th: 'Th', fr: 'Fr', sa: 'Sa' }
export const dayFieldMap = { su: 'sunday', mo: 'monday', tu: 'tuesday', we: 'wednesday', th: 'thursday', fr: 'friday', sa: 'saturday' }
export const mealNameOptions = ['Breakfast', 'Snack', 'Lunch', 'Dinner', 'Pre-workout', 'Post-workout']
export const mealPlanAccents = ['bg-fitness-primary/10 text-fitness-primary', 'bg-emerald-50 text-emerald-600', 'bg-amber-50 text-amber-600', 'bg-blue-50 text-blue-600']
export const defaultFloatPos = { x: 0, y: 0 }
export const defaultLibraryFloatSize = { width: 420 }
export const defaultTotalsFloatSize = { width: 288 }
export const toNumberOrZero = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

export const formatTimeForInput = (value) => {
  if (!value) return ''
  if (typeof value === 'string' && value.includes(':')) return value.slice(0, 5)
  return value
}

export const normalizeTimePayload = (value) => {
  if (!value) return ''
  const trimmed = String(value).trim()
  if (!trimmed) return ''
  if (trimmed.length === 5) return `${trimmed}:00`
  return trimmed
}

export const tryParseJSON = (value) => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch (e) {
    return value
  }
}

export const inferNutritionLibraryNodeKind = (node, children = []) => {
  const normalizedType = String(
    node?.libraryKind || node?.sourceType || node?.source_type || node?.type || node?.item_type || ''
  )
    .trim()
    .toLowerCase()

  if (normalizedType.includes('nutritionprogram') || normalizedType.includes('nutrition program')) return 'template'
  if (normalizedType.includes('mealtemplate') || normalizedType.includes('meal template')) return 'meal'
  if (normalizedType.includes('recipe')) return 'recipe'

  if (node?.nutritionprogram === 1 || node?.nutritionprogram === '1' || node?.nutritionprogram_id || node?.nutritionprogrammealplans) {
    return 'template'
  }
  if (node?.mealtemplate === 1 || node?.mealtemplate === '1' || node?.mealtemplate_id || node?.mealtemplatefoods) {
    return 'meal'
  }
  if (node?.recipe === 1 || node?.recipe === '1' || node?.recipe_id || node?.recipefoods) {
    return 'recipe'
  }

  const hasChildren = Array.isArray(children) && children.length > 0
  const hasGrandchildren = hasChildren && children.some((child) => Array.isArray(child?.children) && child.children.length > 0)

  if (!hasChildren) return 'food'
  if (hasGrandchildren) return 'template'
  return 'meal'
}

export const micronutrientKeyMap = {
  fiber: ['fiber', 'fibers'],
  sugars: ['sugars', 'sugar'],
  calcium: ['calcium', 'calcium_mg'],
  iron: ['iron'],
  magnesium: ['magnesium'],
  phosphorus: ['phosphorus', 'phosphorous'],
  potassium: ['potassium'],
  sodium: ['sodium'],
  zinc: ['zinc'],
}

export const micronutrientDisplayOrder = ['fiber', 'sugars', 'calcium', 'iron', 'magnesium', 'phosphorus', 'potassium', 'sodium', 'zinc']

export const micronutrientLabels = {
  fiber: 'Fiber',
  sugars: 'Sugars',
  calcium: 'Calcium',
  iron: 'Iron',
  magnesium: 'Magnesium',
  phosphorus: 'Phosphorus',
  potassium: 'Potassium',
  sodium: 'Sodium',
  zinc: 'Zinc',
}

export const extractMicronutrients = (food) => {
  const result = {}
  Object.entries(micronutrientKeyMap).forEach(([key, fields]) => {
    const found = fields
      .map((field) => food?.[field])
      .find((val) => val !== undefined && val !== null && Number.isFinite(Number(val)))
    if (found !== undefined && found !== null && Number.isFinite(Number(found))) {
      result[key] = Number(found)
    }
  })
  return result
}

export const aggregateMicronutrients = (items = []) => {
  const totals = {}
  items.forEach((item) => {
    const micros = item?.micronutrients || {}
    Object.entries(micros).forEach(([key, value]) => {
      const num = Number(value)
      if (!Number.isFinite(num)) return
      totals[key] = (totals[key] || 0) + num
    })
  })
  return totals
}

export const getProgramIdForRow = (row, fallback) => row?.program_id || row?.programId || row?.programid || fallback

export const getWeightId = (row) => row?.weight_id || row?.weightId || row?.id

export const getCircumferenceId = (row) => row?.circumference_id || row?.circumferenceId || row?.id

export const getVitalId = (row) => row?.vital_id || row?.vitalId || row?.id

export const firstNonEmptyText = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue
    const text = String(value).trim()
    if (text) return text
  }
  return ''
}

export const resolveExerciseInfoText = (exercise) =>
  firstNonEmptyText(
    exercise?.template?.description,
    exercise?.description,
    exercise?.template?.note,
    exercise?.note,
    exercise?.clientnote
  )

export const resolveExerciseVideoUrl = (exercise) =>
  firstNonEmptyText(exercise?.template?.video_url, exercise?.video_url, exercise?.videoUrl)

export const hasMeaningfulValue = (value) => value !== null && value !== undefined && String(value).trim() !== ''

export const getExercisePairField = (exercise, primary, secondary) => {
  if (hasMeaningfulValue(exercise?.[primary])) return primary
  if (hasMeaningfulValue(exercise?.[secondary])) return secondary
  return primary
}

export const getExercisePairValue = (exercise, primary, secondary) => {
  if (hasMeaningfulValue(exercise?.[primary])) return exercise?.[primary]
  if (hasMeaningfulValue(exercise?.[secondary])) return exercise?.[secondary]
  return ''
}

export const EMPTY_OPTIONS = Object.freeze([])
export const EXERCISE_PAIR_DEFS = Object.freeze([
  { key: 'pair1', primary: 'sets', secondary: 'mode', placeholder: 'Sets/Mode' },
  { key: 'pair2', primary: 'reps', secondary: 'intensity', placeholder: 'Reps/Intensity' },
  { key: 'pair3', primary: 'rest', secondary: 'heartrate', placeholder: 'Rest/HeartRate' },
  { key: 'pair4', primary: 'speed', secondary: 'duration', placeholder: 'Speed/Duration' },
])

export const toFieldBadgeLabel = (fieldName = '') => {
  const value = String(fieldName || '').toLowerCase()
  if (value === 'heartrate') return 'Heart Rate'
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : ''
}

export const formatClientDisplayName = (client) => {
  const name = [client?.firstname, client?.lastname].filter((part) => String(part || '').trim()).join(' ').trim()
  return name || client?.email || `Client ${client?.id ?? ''}`.trim()
}

export const getRowKey = (row, prefix, index) => row?.id || row?.measured_at || row?.guid || `${prefix}-${index}`

export const COMPOSITION_COLUMNS = [
  { key: 'weight', label: 'Weight (lbs)' },
  { key: 'bodyfat', label: 'Body Fat (%)' },
  { key: 'leanmuscle', label: 'Lean Mass (lbs)' },
  { key: 'waterweight', label: 'Water Weight (lbs)' },
  { key: 'fatmass', label: 'Fat Mass (lbs)' },
]

export const CIRCUMFERENCE_COLUMNS = [
  { key: 'neck', label: 'Neck' },
  { key: 'arm', label: 'Arm' },
  { key: 'chest', label: 'Chest' },
  { key: 'waist', label: 'Waist' },
  { key: 'hip', label: 'Hip' },
  { key: 'thigh', label: 'Thigh' },
  { key: 'calf', label: 'Calf' },
  { key: 'forearm', label: 'Forearm' },
]

export const VITALS_COLUMNS = [
  { key: 'resting_heart_rate', label: 'Resting Heart Rate' },
  { key: 'active_heart_rate', label: 'Active Heart Rate' },
  { key: 'resting_blood_pressure', label: 'Systolic BP (mm Hg)', totalsKey: 'systolic_blood_pressure' },
  { key: 'active_blood_pressure', label: 'Diastolic BP (mm Hg)', totalsKey: 'diastolic_blood_pressure' },
  { key: 'o2_saturation', label: 'Oxygen Saturation (%)' },
  { key: 'body_temperature', label: 'Body Temperature (°F)' },
  { key: 'vo2_max', label: 'VO2 Max' },
]

export const toExerciseEmbedUrl = (url) => {
  if (!url) return ''
  try {
    const parsed = new URL(url)
    if (parsed.hostname.includes('vimeo.com')) {
      const parts = parsed.pathname.split('/').filter(Boolean)
      const id = parts.reverse().find((part) => /^\d+$/.test(part))
      if (id) return `https://player.vimeo.com/video/${id}${parsed.search || ''}`
    }
    if (parsed.hostname.includes('youtu.be')) {
      const id = parsed.pathname.replace('/', '')
      if (id) return `https://www.youtube.com/embed/${id}`
    }
    if (parsed.hostname.includes('youtube.com')) {
      const videoId = parsed.searchParams.get('v')
      if (videoId) return `https://www.youtube.com/embed/${videoId}`
      const parts = parsed.pathname.split('/').filter(Boolean)
      const embedIdx = parts.indexOf('embed')
      if (embedIdx !== -1 && parts[embedIdx + 1]) return `https://www.youtube.com/embed/${parts[embedIdx + 1]}`
    }
    return url
  } catch (e) {
    return url
  }
}
